import { createFileRoute } from '@tanstack/react-router';
import { requireCapability } from '@/lib/route-guards';
import { warm, warmBackground } from '@/api/loaders';
import { creativeRevisions, creativesList, productionsList } from '@/api/resources';
import { searchEnum } from '@/lib/search-enum';
import { useSetViewShim } from '@/lib/use-set-view';
import { CreativeLibrary } from '@/screens/library';

const TAB = searchEnum('tab', ['details', 'versions'] as const, 'details');

export type CreativeDrawerTab = (typeof TAB.values)[number];

export const Route = createFileRoute('/creatives/')({
  // `focus` opens a creative's drawer from a link (a campaign slot's "View
  // versions"); `tab` picks which of its tabs. Both live in the URL so the
  // loader can warm the archive the drawer will read.
  validateSearch: (s: Record<string, unknown>): { focus?: string; tab?: CreativeDrawerTab } => ({
    focus: typeof s.focus === 'string' && s.focus.length > 0 ? s.focus : undefined,
    tab: s.tab === undefined ? undefined : TAB.parse(s),
  }),
  loaderDeps: ({ search }) => ({ focus: search.focus }),
  // Search-driven route: no skeleton for an in-screen navigation (router.ts).
  pendingMs: Infinity,
  beforeLoad: requireCapability('creatives_view'),
  // Blocks on the rows this screen is about; its companions fill in behind.
  loader: ({ context, deps }) => {
    warmBackground(context.queryClient, productionsList({}));
    if (deps?.focus) warmBackground(context.queryClient, creativeRevisions(deps.focus));
    return warm(context.queryClient, creativesList({}));
  },
  component: Page,
});

function Page() {
  const setView = useSetViewShim();
  const { focus, tab } = Route.useSearch();
  return (
    <CreativeLibrary
      setView={setView}
      flashId={undefined}
      clearFlash={() => {}}
      focus={focus}
      initialTab={tab ?? 'details'}
    />
  );
}
